import type { Table } from '@tanstack/react-table';

import { forwardRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { cva } from 'class-variance-authority';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Button } from '@paid-ui/components/button';
import { DropdownMenu } from '@paid-ui/components/dropdown-menu';
import { IconButton } from '@paid-ui/components/icon-button';
import { Select, SelectItem } from '@paid-ui/components/select';
import { ArrowRightIcon } from '@paid-ui/icons/arrow-right';
import { ChevronLeftIcon } from '@paid-ui/icons/chevron-left';
import { ChevronRightIcon } from '@paid-ui/icons/chevron-right';
import { ChevronsLeftIcon } from '@paid-ui/icons/chevrons-left';
import { ChevronsRightIcon } from '@paid-ui/icons/chevrons-right';

export function Table({
  hidden,
  changed,
  viewportRef,
  bordered = true,
  className,
  ...props
}: React.ComponentProps<'table'> & {
  bordered?: boolean;
  changed?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
}) {
  if (hidden) {
    return null;
  }

  return (
    <ScrollArea.Root
      className={twMerge(
        'relative w-full overflow-hidden scroll-smooth bg-white',
        bordered ? 'rounded-md border' : 'rounded-none border-none',
        changed ? 'border-2 border-purple' : 'border-grey-mid',
      )}
    >
      <ScrollArea.Viewport ref={viewportRef} className="h-full w-full rounded-md">
        <table
          className={twMerge(
            'min-w-full table-fixed caption-bottom border-collapse text-sm',
            className,
          )}
          {...props}
        />
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        className="z-10 flex touch-none select-none bg-grey-hover p-0.5 transition-colors ease-out hover:bg-grey-mid data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col"
        orientation="horizontal"
      >
        <ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-grey-tertiary before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2" />
      </ScrollArea.Scrollbar>
      <ScrollArea.Corner className="bg-grey-mid" />
    </ScrollArea.Root>
  );
}

export function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      className={twMerge('bg-grey-light [&_tr]:border-b [&_tr]:border-grey-mid', className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody className={twMerge('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      className={twMerge(
        'border-t border-grey-mid bg-grey-light font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

export const TableRow = forwardRef<
  HTMLTableRowElement,
  React.ComponentProps<'tr'> & { hoverable?: boolean }
>(({ hidden, className, hoverable = true, ...props }, ref) => {
  if (hidden) {
    return null;
  }

  return (
    <tr
      ref={ref}
      className={twMerge(
        'group/row border-b border-grey-mid transition-colors data-[state=selected]:bg-grey-light',
        hoverable ? 'hover:bg-grey-light' : '',
        className,
      )}
      {...props}
    />
  );
});

TableRow.displayName = 'TableRow';

const headVariants = cva(
  'text-inc whitespace-nowrap px-5 py-3.5 text-left align-middle font-medium [&:has([role=checkbox])]:px-3 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      sticky: {
        left: "sticky left-0 z-10 bg-grey-light after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-grey-mid after:content-['']",
        right:
          "sticky right-0 z-10 bg-grey-light after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-grey-mid after:content-['']",
      },
      bordered: {
        true: 'border-l border-grey-mid',
      },
    },
  },
);

export function TableHead({
  hidden,
  sticky,
  bordered,
  className,
  ...props
}: React.ComponentProps<'th'> & {
  sticky?: 'left' | 'right';
  bordered?: boolean;
}) {
  if (hidden) {
    return null;
  }

  return <th className={twMerge(headVariants({ sticky, bordered, className }))} {...props} />;
}

const cellVariants = cva(
  'whitespace-nowrap px-5 py-3.5 align-middle [&:has([role=checkbox])]:px-3 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      sticky: {
        left: "sticky left-0 z-10 bg-white after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-grey-mid after:content-[''] group-hover/row:bg-grey-light",
        right:
          "sticky right-0 z-10 bg-white after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-grey-mid after:content-[''] group-hover/row:bg-grey-light",
      },
      bordered: {
        true: 'border-l border-grey-mid',
      },
    },
  },
);

export function TableCell({
  hidden,
  sticky,
  bordered,
  className,
  ...props
}: React.ComponentProps<'td'> & {
  sticky?: 'left' | 'right';
  bordered?: boolean;
}) {
  if (hidden) {
    return null;
  }

  return <td className={twMerge(cellVariants({ sticky, bordered, className }))} {...props} />;
}

export function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption className={twMerge('mt-4 text-sm text-grey-dark', className)} {...props} />;
}

export const TablePagination: React.FC<{ table: Table<any>; hideSelectedCount?: boolean }> = ({
  table,
  hideSelectedCount,
}) => {
  return (
    <div className="flex flex-wrap items-center justify-between gap-y-4 px-2">
      {hideSelectedCount ? null : (
        <div className="text-inc flex-1 text-sm font-medium">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
      )}
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="shrink-0 text-sm font-medium">Rows per page</p>
          <Select
            compact
            className="h-8 text-sm"
            sideOffset={4}
            placeholder={table.getState().pagination.pageSize.toString()}
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            {[5, 10, 20, 50, 100].map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`} className="text-sm">
                {pageSize}
              </SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const TableScrollTip: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={twMerge('flex items-center justify-center', className)}>
      <div className="inline-flex items-center gap-x-1.5 rounded-[10px] bg-grey-hover px-4 py-2.5 text-sm text-grey-dark">
        <span>Tip: Press </span>
        <span className="font-medium text-black">Shift + Scroll</span>
        <span> to scroll horizontally</span>
        <ArrowRightIcon />
      </div>
    </div>
  );
};

export interface SortOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  ascIcon?: React.ComponentType<{ className?: string }>;
  descIcon?: React.ComponentType<{ className?: string }>;
}

export interface TableSortControlProps {
  options: SortOption[];
  defaultSortKey?: string;
  defaultDirection?: 'ASC' | 'DESC';
  sortKeyParam?: string;
  sortDirectionParam?: string;
  value?: string;
  direction?: 'ASC' | 'DESC';
  onChange?: (key: string, direction: 'ASC' | 'DESC') => void;
  className?: string;
}

export const TableSortControl: React.FC<TableSortControlProps> = ({
  options,
  defaultSortKey,
  defaultDirection = 'DESC',
  sortKeyParam = 'sortByKey',
  sortDirectionParam = 'sortByDirection',
  value,
  direction,
  onChange,
  className,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isControlled = onChange !== undefined;

  const sortKey = isControlled
    ? (value ?? defaultSortKey ?? options[0]?.value ?? '')
    : (searchParams.get(sortKeyParam) ?? defaultSortKey ?? options[0]?.value ?? '');
  const sortDirection = isControlled
    ? (direction ?? defaultDirection)
    : ((searchParams.get(sortDirectionParam) as 'ASC' | 'DESC') ?? defaultDirection);

  const updateParams = (nextKey: string, nextDirection: 'ASC' | 'DESC') => {
    if (isControlled) {
      onChange(nextKey, nextDirection);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set(sortKeyParam, nextKey);
    next.set(sortDirectionParam, nextDirection);
    router.push({ pathname, query: next.toString() }, undefined, { shallow: true, scroll: false });
  };

  const activeOption = options.find((o) => o.value === sortKey);
  const isActive = sortKey !== (defaultSortKey ?? options[0]?.value);
  const iconClassName = 'h-4 w-4 shrink-0';

  const getDirectionIcon = (option: SortOption, dir: 'ASC' | 'DESC') =>
    dir === 'ASC' ? (option.ascIcon ?? ArrowUpIcon) : (option.descIcon ?? ArrowDownIcon);

  const sortItems = options.map((option) => {
    const isSelected = sortKey === option.value;
    const Icon = isSelected
      ? getDirectionIcon(option, sortDirection)
      : (option.icon ?? ArrowUpDownIcon);
    return {
      title: (
        <span className="flex items-center gap-x-2">
          <Icon className={iconClassName} />
          {option.label}
        </span>
      ),
      selected: isSelected,
      onSelect: () =>
        isSelected
          ? updateParams(option.value, sortDirection === 'ASC' ? 'DESC' : 'ASC')
          : updateParams(option.value, sortDirection),
    };
  });

  const TriggerIcon = activeOption
    ? getDirectionIcon(activeOption, sortDirection)
    : ArrowUpDownIcon;

  return (
    <DropdownMenu
      items={sortItems}
      align="end"
      showArrow={false}
      className={twMerge('border border-grey-mid', className)}
    >
      <IconButton size="lg" variant={isActive ? 'solid' : 'outline'} className="shrink-0">
        <TriggerIcon className="h-5 w-5" />
      </IconButton>
    </DropdownMenu>
  );
};
